import type { AgreementPaymentSlot } from "../../features/import/types";

// Maps a Payment status to a fill colour for the inner coloured bar within the grey track.
// Backend emits PaymentStatus TextChoices *values* (lowercase), not the constant names.
//   done                         → green  (this month is fully paid)
//   delayed_with_child_payments  → amber  (partial fill = paid/amount proportion)
//   everything else              → grey track only (no fill)
function fillClass(status: string): string {
  // Brand green for the "paid" segment; amber-400 kept for the "partial" segment because the
  // brand palette has no mid-amber token (warning #B54708 is too dark for a 2.5px viz fill).
  if (status === "done") return "bg-green";
  return "bg-amber-400";
}

/**
 * Compute fill ratio for a slot:
 *   done                        → 1 (full green fill)
 *   delayed_with_child_payments → clamp(paid/amount, 0, 1) — proportional amber fill
 *   everything else             → 0 (grey track only, no inner fill)
 */
function fillRatio(slot: AgreementPaymentSlot): number {
  if (slot.status === "done") return 1;
  if (slot.status === "delayed_with_child_payments") {
    const amount = Number(slot.amount);
    const paid = Number(slot.paid ?? 0);
    if (amount > 0) return Math.min(1, Math.max(0, paid / amount));
  }
  return 0;
}

/**
 * Segmented payment-progress bar: one segment per scheduled month, coloured by payment status.
 * Each segment is a grey track with an inner coloured fill proportional to how much is paid:
 *   done → full green; delayed_with_child_payments → proportional amber (paid/amount); else empty.
 * Purely presentational — the caller supplies the real schedule (non-uniform per task 0080) and
 * renders its own legend. Renders nothing when there are no slots. The native `title` tooltip
 * shows the month number, status and amount (partial months show paid/amount) for quick inspection.
 */
export function ProgressBar({ slots }: { slots: AgreementPaymentSlot[] }) {
  if (!slots.length) return null;
  return (
    <div className="flex gap-0.5" role="img" aria-label="payment progress">
      {slots.map((slot, i) => {
        const ratio = fillRatio(slot);
        return (
          <div
            key={slot.number ?? i}
            className="relative h-2.5 flex-1 overflow-hidden rounded-sm bg-grey-200"
            title={`${slot.number ? `#${slot.number}` : ""} ${slot.status} · ${
              slot.status === "delayed_with_child_payments"
                ? `${slot.paid}/${slot.amount}`
                : slot.amount
            }`.trim()}
          >
            {ratio > 0 && (
              <div
                className={`absolute inset-y-0 left-0 ${fillClass(slot.status)}`}
                style={{ width: `${ratio * 100}%` }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
